<template>
  <b-modal
    :ref="modalRef"
    :id="modalId"
    size="lg"
    :title="title"
    @show="onShow"
    @hide="onHide"
    centered
    class="m-0 p-0"
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-overlay :show="isLoading">
      <div>Felder zum Bearbeiten auswählen:</div>
      <div class="d-flex align-items-center mt-2" style="gap: 8px">
        <AppSelect
          v-model="selectedFieldKeys"
          :options="fieldEditSelectOptions"
          label="Felder"
          placeholder="Feld auswählen..."
          search-placeholder="Feld suchen …"
          multiple
          clearable
          class="w-100"
        />
      </div>

      <div class="mt-4 fields-table" v-if="fieldsToEdit.length > 0">
        <div v-for="item in fieldsToEdit" :key="item.key" class="field-row">
          <div class="field-label">{{ item.label }}</div>
          <div class="field-input">
            <b-form-input
              v-if="item.type === 'number'"
              size="sm"
              v-model="item.value"
              :number="true"
              type="number"
              min="0"
              max="365"
              step="1"
              required
            ></b-form-input>

            <b-form-input
              v-else-if="item.type === 'input'"
              size="sm"
              v-model="item.value"
              placeholder="-"
            ></b-form-input>

            <AppSelect
              v-else-if="item.type === 'select'"
              v-model="item.value"
              sort="none"
              :options="item.selectOptions"
              :label="item.label"
              placeholder="-"
              hide-search
              clearable
              size="sm"
            />

            <AirportsSucheMultiselect
              v-else-if="item.type === 'flughafen'"
              v-model="item.value"
              :isMultipleAllowed="false"
              :openDirection="'top'"
              id="abflughafen"
              :filterCountry="country"
            ></AirportsSucheMultiselect>
          </div>
        </div>
      </div>
    </b-overlay>
    <template #modal-footer="{ hide }" class="pl-1">
      <b-button class="dialog-btn-secondary" @click="hide">Abbrechen</b-button>
      <b-button
        class="dialog-btn"
        @click="handleSubmit(hide)"
        :disabled="fieldsToEdit.length === 0"
        variant="primary"
      >
        Speichern
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import AirportsSucheMultiselect from '@/components/common/airports-suche-multiselect.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import apiService from '@/core/common/services/api.service';

export default {
  props: {
    title: { type: String, default: 'Reisetermine bearbeiten' },
    isLoading: { type: Boolean, default: false },
    country: { type: String, default: null },
    editOptions: {
      type: Array,
      required: true,
    },
    modalRef: {
      type: String,
      required: true,
    },
    modalId: {
      type: String,
      required: true,
    },
  },
  components: {
    AirportsSucheMultiselect,
    AppSelect,
  },
  computed: {
    // Das Select arbeitet mit den Feldschlüsseln, die Felder selbst behalten ihre Werte
    fieldEditSelectOptions() {
      return this.editOptions.map(option => ({ value: option.key, text: option.label }));
    },
    selectedFieldKeys: {
      get() {
        return this.fieldsToEdit.map(feld => feld.key);
      },
      set(keys) {
        this.fieldsToEdit = keys.map(key => {
          const bestehend = this.fieldsToEdit.find(feld => feld.key === key);
          return bestehend ?? { ...this.editOptions.find(option => option.key === key) };
        });
      },
    },
  },
  data() {
    return {
      fieldsToEdit: [],
      resultsAirlines: [],
    };
  },
  watch: {
    fieldsToEdit: {
      handler(fields, oldFields) {
        const fieldKeys = fields.map(field => field.key);
        const oldFieldKeys = oldFields.map(field => field.key);

        if (!oldFieldKeys.includes('flugnummerGruppeHin') && fieldKeys.includes('flugnummerGruppeHin')) {
          const autoFields = ['optionsfrist', 'ticketfrist'];

          autoFields.forEach(fieldKey => {
            if (!fieldKeys.includes(fieldKey)) {
              const fieldToAdd = this.editOptions.find(option => option.key === fieldKey);
              if (fieldToAdd) {
                this.fieldsToEdit.push(fieldToAdd);
              }
            }
          });
        }

        const flugnummerGruppeHinField = this.fieldsToEdit.find(field => field.key === 'flugnummerGruppeHin');

        const flugnummerHin = flugnummerGruppeHinField ? flugnummerGruppeHinField.value : null;

        const checkFlugNumber = async flugNr => {
          if (flugNr && flugNr?.length >= 2) {
            const airlineKuerzel = flugNr.substring(0, 2);
            if (!this.resultsAirlines.some(result => result.airlineKuerzel === airlineKuerzel)) {
              try {
                const res = await apiService.get(`airline?$filter=airlineKuerzel eq '${airlineKuerzel}'`);
                const { result } = res.data;
                if (result.length === 1) {
                  this.resultsAirlines.push(result[0]);
                  this.setFristen(result[0].optionsfrist, result[0].ticketingfrist);
                } else {
                  this.resultsAirlines.push({ airlineKuerzel });
                }
              } catch (error) {
                console.error('Fehler beim Abrufen der Airline-Daten:', error);
              }
            }
          }
        };
        checkFlugNumber(flugnummerHin);
      },
      deep: true,
    },
  },
  methods: {
    onShow() {
      // es wird überprüft, ob es deafault Fields gibt
      this.fieldsToEdit = this.editOptions.filter(option => option.isDefault);
    },
    onHide() {
      this.$emit('hide');
    },
    handleSubmit() {
      this.$emit('handleSubmit', this.fieldsToEdit);
    },
    setFristen(optionsfrist, ticketingfrist) {
      const optionsfristField = this.fieldsToEdit.find(field => field.key === 'optionsfrist');
      const ticketingfristField = this.fieldsToEdit.find(field => field.key === 'ticketfrist');

      if (optionsfristField) {
        this.$set(optionsfristField, 'value', optionsfrist);
      }
      if (ticketingfristField) {
        this.$set(ticketingfristField, 'value', ticketingfrist);
      }
    },
  },
};
</script>
<style scoped>
.fields-table {
  border-top: 1px solid var(--border);
}
.field-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border);
  padding: 8px 0;
}
.field-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.field-label {
  width: 160px;
  flex-shrink: 0;
  font-weight: 500;
  font-size: 14px;
  padding-left: 8px;
}
.field-input {
  flex: 1;
}
.field-input :deep(.input-group-text) {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>
